import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// graphql
import {
  BUSINESS_ACTIVITIES_TREE_UPDATE_MUTATION,
  BUSINESS_ACTIVITY_DELETE_MUTATION
} from 'graphql/businessActivity/mutations'
// components
import Grid from '@mui/material/Grid2'
import Tree from 'components/Tree'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// context
import { BusinessActivitiesAPIContext } from 'containers/BusinessActivities/layers/API'
// themes
import { ActiveLink } from 'themes/views/tree'
import { REACT_SORTABLE_TREE_STYLES } from 'themes'
// constants
import { Modals } from 'constants/modals'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const Placeholder = ({ handleAddChild }) => {
  return (
    <Grid container justifyContent={'center'} alignItems={'center'} direction={'column'}>
      <Grid>There is no business activity here.</Grid>
      <Grid>
        Click <ActiveLink onClick={() => handleAddChild({})}>here</ActiveLink> to create your first business activity
      </Grid>
    </Grid>
  )
}

const BusinessActivitiesTree = () => {
  const [deleteMutation] = useMutation(BUSINESS_ACTIVITY_DELETE_MUTATION)
  const [treeUpdateMutation] = useMutation(BUSINESS_ACTIVITIES_TREE_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.BUSINESS_ENTITY_MODAL })
  const { showModal: showConfirm } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })

  const { businessActivities, isLoading, refetch } = React.useContext(BusinessActivitiesAPIContext)

  const synchronizeTree = async (tree) => {
    try {
      await treeUpdateMutation({
        variables: {
          businessActivities: tree.map(({ id, parentId, position, expanded }) => ({
            id,
            parentId,
            position,
            expanded
          }))
        }
      })
    } catch (errors) {
      console.log(errors)
    }
  }

  const handleDelete = ({ node }) =>
    showConfirm({
      width: 'sm',
      handleConfirm: async () => {
        try {
          await deleteMutation({
            variables: {
              id: node.id
            }
          })

          await refetch()
        } catch (errors) {
          console.log('[BusinessActivitiesTree.handleDelete]: ', parseGraphQLErrorMessages(errors))
        }
      }
    })

  const handleEdit = ({ node }) =>
    showModal({
      width: 'sm',
      item: node
    })

  const handleAddChild = ({ node }) =>
    showModal({
      width: 'sm',
      parent: node
    })

  const actionsHandlers = () => ({
    actionsHandlers: {
      handleEditClick: ({ node }) => handleEdit({ node }),
      handleDeleteClick: ({ node }) => handleDelete({ node }),
      handleAddChildClick: ({ node }) => handleAddChild({ node })
    }
  })

  return (
    <>
      <Tree
        refetch={refetch}
        isLoading={isLoading}
        source={businessActivities}
        synchronizeTree={synchronizeTree}
        generateActionHandlers={actionsHandlers}
        placeholder={<Placeholder handleAddChild={handleAddChild} />}
        reactVirtualizedListProps={{
          style: REACT_SORTABLE_TREE_STYLES
        }}
      />
      <ConfirmDeleteModal header={'Delete Business Activity'}>
        Are you sure you want to delete this business activity?
      </ConfirmDeleteModal>
    </>
  )
}

export default BusinessActivitiesTree
